import { useClient } from "creso-core/hooks";
import {
  ClientStatus,
  FamilyAumMember,
  FamilyAumOverview,
  FamilySource,
} from "creso-core/types";
import { formatAsCrore } from "creso-core/utils";
import { ValueVector } from "@core/components/value-vector";
import { ArrowUpRight } from "lucide-react";
import { TransactionTrigger } from "@core/components/transaction-trigger";
import { Button, cn, ScrollArea, Separator } from "web-design-system";
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { MemberBar } from "./member-bar";
import { ImportHoldingsDialogContent } from "../portfolio/import-holdings/dialog";
import {
  BreakdownColumn,
  BreakdownTable,
  getColumnAlignClass,
  GRID_TEMPLATE_CLASS,
  MetricValue,
} from "./common";

function FetchHoldingsDialog({
  clientId,
  onClose,
}: {
  clientId: number;
  onClose: () => void;
}) {
  const { data: client } = useClient(clientId);

  if (!client) return null;

  return <ImportHoldingsDialogContent client={client} onClose={onClose} />;
}

function getAumBreakdownColumns(
  source: FamilySource,
  onFetchHoldings: (clientId: number) => void,
  clientStatusById: Map<number, ClientStatus>
): BreakdownColumn<FamilyAumMember>[] {
  return [
    {
      id: "member",
      render: (row) => (
        <MemberBar
          client_id={row.client_id}
          name={row.display_name}
          holdingPattern={row.holding_pattern}
          value={formatAsCrore(row.current_value ?? 0, 1)}
          percentage={row.share_pct ?? 0}
        />
      ),
    },
    {
      id: "xirr",
      render: (row) =>
        row.lifetime_xirr != null ? (
          <ValueVector value={row.lifetime_xirr} precision={1} />
        ) : null,
    },
    {
      id: "invested_value",
      render: (row) => {
        const status = row.client_id
          ? clientStatusById.get(row.client_id)
          : undefined;

        if (status === "SETUP_PENDING" && row.client_id) {
          return (
            <Link
              to="/clients/$id"
              params={{ id: String(row.client_id) }}
              className="inline-flex items-center gap-1 text-sm text-primary"
              target="_blank"
            >
              Finish setup
              <ArrowUpRight className="size-3.5" />
            </Link>
          );
        }

        if (row.invested_value) {
          return (
            <span className="text-sm font-semibold">
              {formatAsCrore(row.invested_value, 1)}
            </span>
          );
        }

        if (!row.client_id || row?.status === "SETUP_PENDING") {
          return <span className="text-sm text-muted-foreground">-</span>;
        }

        if (source !== "EXTERNAL") {
          return (
            <TransactionTrigger
              size="sm"
              label="Transact"
              clientId={row.client_id}
            />
          );
        }

        return (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onFetchHoldings(row.client_id as number)}
          >
            Fetch Holdings
          </Button>
        );
      },
    },
  ];
}

export function AumSummaryCard({
  overview,
  source,
  clientStatusById,
}: {
  overview?: FamilyAumOverview;
  source: FamilySource;
  clientStatusById: Map<number, ClientStatus>;
}) {
  const [fetchHoldingsClientId, setFetchHoldingsClientId] = useState<
    number | null
  >(null);

  const summary = overview?.summary;
  const members = overview?.members ?? [];

  const metrics = useMemo(() => {
    return [
      {
        label: "Current Value",
        value: (
          <div className="flex items-end gap-2 font-semibold">
            <span>{formatAsCrore(summary?.current_value ?? 0, 1)}</span>
            {summary?.current_value_pct != null ? (
              <ValueVector
                className="font-medium pb-[1px]"
                value={summary.current_value_pct}
                precision={1}
              />
            ) : null}
          </div>
        ),
      },
      {
        label: "XIRR",
        value:
          summary?.lifetime_xirr != null ? (
            <ValueVector value={summary.lifetime_xirr} precision={1} />
          ) : (
            "-"
          ),
      },
      {
        label: "Invested",
        value: (
          <span className="font-semibold">
            {formatAsCrore(summary?.invested_value ?? 0, 1)}
          </span>
        ),
      },
    ];
  }, [summary]);

  const breakdownColumns = useMemo(
    () =>
      getAumBreakdownColumns(
        source,
        setFetchHoldingsClientId,
        clientStatusById
      ),
    [source, clientStatusById]
  );
  const totalColumns = metrics.length;
  const gridClassName =
    GRID_TEMPLATE_CLASS[totalColumns] ?? GRID_TEMPLATE_CLASS[3];

  return (
    <div className="rounded-xl bg-background p-4 flex-1 min-w-0">
      <div className={"grid gap-y-4 pr-2"}>
        <div className={cn("grid grid-cols-3 mr-2", gridClassName)}>
          {metrics.map((metric, index) => (
            <div
              key={metric.label}
              className={cn(getColumnAlignClass(index, metrics.length))}
            >
              <MetricValue {...metric} />
            </div>
          ))}
        </div>
        <Separator className="col-span-full" />
        {members.length > 0 ? (
          <ScrollArea className="col-span-full max-h-[210px]">
            <BreakdownTable
              rows={members}
              columns={breakdownColumns}
              gridClassName={gridClassName}
            />
          </ScrollArea>
        ) : null}
      </div>
      {fetchHoldingsClientId != null ? (
        <FetchHoldingsDialog
          clientId={fetchHoldingsClientId}
          onClose={() => setFetchHoldingsClientId(null)}
        />
      ) : null}
    </div>
  );
}
